/**
 * Shared bits of the developer API data services (artist + venue shows).
 */

import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import dayjs from "@punchup/utils/dayjs";

import { ShowComedianRow } from "../../legacy/shows";
import { DeveloperApiLineupMember } from "../../models/developer-api.types";

/**
 * Resolves which of a set of Tixologi event ids must be hidden. Injected by the
 * route from `@punchup/tixologi` (`getHiddenTixologiEventIds`), which cannot be
 * imported here without a dependency cycle.
 */
export type HiddenEventIdResolver = (
  eventIds: string[]
) => Promise<Set<string>>;

/** The columns the public-visibility rule reads off a comedian row. */
export interface PublicVisibilityColumns {
  is_punchup_verified: boolean | null;
  is_external_preview: boolean | null;
  published_at: string | null;
}

/**
 * Whether one comedian lets a show be public. A verified comedian gates the
 * show on their own publishing state; an unverified one never blocks it.
 * Mirrors `showCanBeRendered` in getShows - the rule the comedian pages use.
 */
export function isPubliclyVisible(
  comedian: PublicVisibilityColumns | null | undefined
): boolean {
  if (!comedian?.is_punchup_verified) {
    return true;
  }

  return (
    !comedian.is_external_preview && dayjs().isAfter(comedian.published_at)
  );
}

/** The public Punchup purchase page for a show - the contract's ticketUrl. */
export function punchupShowUrl(showId: string): string {
  return `https://punchup.live/show/${showId}`;
}

/** The public Punchup profile page for an artist slug. */
export function punchupComedianUrl(slug: string): string {
  return `https://punchup.live/${slug}`;
}

/**
 * Map a show's comedian pairings into the contract's lineup, sorted by the
 * pairing's `ordering` - the sequence the venue entered the bill in, which is
 * all the schema records. DEFAULT-DENY like the show transforms: never spread
 * the pairing row.
 *
 * `slug` and `profileUrl` are populated only for a verified artist, matching
 * the rule punchup.live's own lineup UI uses to decide whether to link out; an
 * unverified performer still appears in the lineup, just without a profile.
 * The two answer the same question - is there a public profile to point at -
 * so they are derived from one value and can never disagree. Emitting a slug
 * for an unpublished performer would hand a consumer the parts to build a URL
 * the deliberate `null` profileUrl is withholding.
 *
 * The lineup drops a verified comedian who is not yet public: punchup.live
 * does not show them, so the feed must not hand out their bio and headshot
 * either. The show itself stays; only the pairing leaves. A pairing with
 * `hidden_from_comedian_page` stays: that flag keeps the show off that
 * comedian's own page, and the public venue page and event page still list
 * them on the bill.
 */
export function toDeveloperApiLineup(
  showComedians: ShowComedianRow[] | null | undefined
): DeveloperApiLineupMember[] {
  return [...(showComedians ?? [])]
    .filter(isPubliclyVisible)
    .sort((a, b) => a.ordering - b.ordering)
    .map((comedian, index) => {
      const publicSlug =
        comedian.is_punchup_verified && comedian.slug ? comedian.slug : null;

      return {
        id: comedian.id,
        name: comedian.display_name ?? "",
        shortName: comedian.display_name_short || null,
        slug: publicSlug,
        profileUrl: publicSlug ? punchupComedianUrl(publicSlug) : null,
        imageUrl: comedian.about_img
          ? getCDNImageURL(comedian.about_img)
          : null,
        bio: comedian.about || null,
        isVerified: !!comedian.is_punchup_verified,
        position: index,
      };
    });
}
